/**
 * Narrows a filter's options to the markets currently selected, so picking a
 * market stops the Case Owner / Case Vendor lists from offering people and
 * vendors from every other market (OPS-2435).
 *
 * Two deliberate escape hatches, both there so scoping can only ever remove
 * noise and never a usable choice:
 *  - an option the API can't classify (no `baseInfo`, or an empty `regionIds`)
 *    is kept, so a gap in the vendor→region data degrades to today's behavior
 *    instead of hiding a real option;
 *  - an option that is already selected is kept even when it belongs to
 *    another market, so a filter carried over from a previous market stays
 *    visible in the panel and can still be unchecked.
 *
 * Fine for Case Vendors (~2/3 carry regionIds, from their SFDC account). NOT
 * enough for Case Owners — see filterOwnerOptionsByRegions.
 *
 * Lives outside the store so it stays importable from tests — the store module
 * pulls in route/localStorage side effects at import time.
 */
export const filterOptionsByRegions = <
  T extends { id: string; baseInfo?: { regionIds: string[] } },
>(
  options: T[],
  regionIds?: string[],
  selectedIds?: string[],
): T[] => {
  if (!regionIds?.length) return options
  const selectedRegions = new Set(regionIds)
  const keep = new Set(selectedIds ?? [])
  return options.filter(option => {
    if (keep.has(option.id)) return true
    const optionRegions = option.baseInfo?.regionIds
    if (!optionRegions?.length) return true
    return optionRegions.some(regionId => selectedRegions.has(regionId))
  })
}

/**
 * Region scoping for the Case Owner options. Owners are mostly internal staff,
 * and their vendor records almost never carry `regionIds` (measured on stage:
 * 15 of 203) — so the vendor-style scoping above degrades into keeping nearly
 * everyone. The authoritative staff↔market association is the market roster
 * (`staff.searchStaff`), matched here by email; the vendor-region match is
 * kept as a secondary signal for owner records that do carry regions.
 *
 * `rosterEmails` is null/undefined while the roster is loading or when its
 * fetch failed — then this falls back to the conservative vendor-style
 * scoping, so a roster outage can never blank the filter.
 */
export const filterOwnerOptionsByRegions = <
  T extends { id: string; email?: string; baseInfo?: { regionIds: string[] } },
>(
  options: T[],
  regionIds?: string[],
  selectedIds?: string[],
  rosterEmails?: ReadonlySet<string> | null,
): T[] => {
  if (!regionIds?.length) return options
  if (!rosterEmails) return filterOptionsByRegions(options, regionIds, selectedIds)
  const selectedRegions = new Set(regionIds)
  const keep = new Set(selectedIds ?? [])
  return options.filter(option => {
    if (keep.has(option.id)) return true
    if (option.email && rosterEmails.has(option.email.trim().toLowerCase())) return true
    return Boolean(option.baseInfo?.regionIds?.some(regionId => selectedRegions.has(regionId)))
  })
}
